export type * from "./types.ts";
export { bridgeScript, injectBridge } from "./bridge.ts";
export { mountGame, normalizeGameMessage } from "./host.ts";
export type { GameHandle, MountOptions } from "./host.ts";
export {
  ValidationRecorder, RecordingContractError, buildStartBody, buildCompletionBody, parseRecordingResponse, isRetryable,
  utc, parseUtc, SERVER_MODULES, VALIDATION_TYPES, MAX_BODY_BYTES, MAX_RESPONSE_BYTES, MAX_PARTICIPANT_ID, MAX_RECORDING_INDEX,
} from "./validation.ts";
export type {
  RecordingCompletion, RecordingLevel, RecordingEventMarker, RecordingScore, RecordingReceipt, RecordingResponse, ReceiptStatus,
  RecordingPost, RecordingStore, RecorderState, RecorderPhase, RecorderOptions, StartRecordingOptions,
} from "./validation.ts";
export { BlockChoiceScore, formatChoiceScore, DEFAULT_CHOICE_WINDOW, DEFAULT_CHOICE_WEIGHTS } from "./choice-score.ts";
export type { ChoiceScoreOptions, ChoiceScoreSnapshot } from "./choice-score.ts";
export { createZip, readZip, crc32 } from "./zip.ts";
export type { ZipEntry, ZipOptions } from "./zip.ts";
export {
  TRAINING_PROGRAMS, MAX_TRAINING_ZIP_BYTES, TrainingUploadError, uploadTimestamp, checkTrainingUpload, buildTrainingUploadForm,
  parseTrainingUploadResponse, isUploadRetryable, sha256Hex, receiptMismatch, uploadTrainingZip,
} from "./training-upload.ts";
export type {
  TrainingUploadInput, TrainingUploadReceipt, TrainingUploadResponse, TrainingUploadPost, TrainingUploadResult,
} from "./training-upload.ts";

import type { GameManifest } from "./types.ts";
import { TRAINING_PROGRAMS } from "./training-upload.ts";

/** Identity helper that gives manifests type checking and a single import for authors. */
export function defineManifest(manifest: GameManifest): GameManifest {
  const ids = new Set<string>();
  for (const level of manifest.levels) {
    if (ids.has(level.id)) throw new Error(`defineManifest(${manifest.id}): duplicate level id "${level.id}"`);
    ids.add(level.id);
    const v = level.validation;
    if (v && (!Number.isInteger(v.gameLevel) || v.gameLevel < 1 || v.gameLevel > 100)) {
      throw new Error(`defineManifest(${manifest.id}): level "${level.id}" validation.gameLevel must be 1 to 100`);
    }
  }
  const gameLevels = manifest.levels.flatMap((l) => (l.validation ? [l.validation.gameLevel] : []));
  if (new Set(gameLevels).size !== gameLevels.length) throw new Error(`defineManifest(${manifest.id}): duplicate validation.gameLevel`);
  if (gameLevels.length && !manifest.validation?.module) throw new Error(`defineManifest(${manifest.id}): validation.module is required`);
  for (const level of manifest.levels) {
    const name = level.validation?.moduleName;
    if (name === undefined) continue;
    const program = manifest.validation?.program;
    const modules = program && Object.prototype.hasOwnProperty.call(TRAINING_PROGRAMS, program) ? TRAINING_PROGRAMS[program] : undefined;
    if (!modules) throw new Error(`defineManifest(${manifest.id}): validation.program must be one of ${Object.keys(TRAINING_PROGRAMS).join(", ")}`);
    if (!modules.includes(name)) throw new Error(`defineManifest(${manifest.id}): level "${level.id}" moduleName "${name}" is not a ${program} module`);
  }
  return manifest;
}
